import React, { useEffect, useRef, useState } from 'react';
import { Box, Collapse, ListItemButton, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FINANCE,
  FINANCE_CONTRACTS,
  FINANCE_DEBTS,
  FINANCE_INVOICES,
  FINANCE_PLATFORM_ITEMS,
  FINANCE_SETTINGS
} from '../common/constants/routes.constants';
import { useUserStore } from '../store/useUserStore';
import { useSidebarStore } from '../store/useSidebarStore';

import { palette } from '../theme';
interface FinanceNavItem {
  labelKey: string;
  route: string;
  /** Which paths count as this page - detail pages light up the list they belong to. */
  isActive: (pathname: string) => boolean;
  superAdminOnly?: boolean;
}

const under = (pathname: string, base: string) => pathname === base || pathname.startsWith(`${base}/`);

const FINANCE_ITEMS: FinanceNavItem[] = [
  {
    labelKey: 'nav.financeItems.overview',
    route: FINANCE,
    // the location and group drill-downs are views of the overview, not pages of their own
    isActive: pathname => pathname === FINANCE || pathname.startsWith(`${FINANCE}/location/`)
  },
  {
    labelKey: 'nav.financeItems.invoices',
    route: FINANCE_INVOICES,
    // an invoice and a generation run are opened from the Invoices list, so it stays lit
    isActive: pathname =>
      under(pathname, FINANCE_INVOICES) || pathname.startsWith(`${FINANCE}/invoice/`) || pathname.startsWith(`${FINANCE}/batch/`)
  },
  { labelKey: 'nav.financeItems.contracts', route: FINANCE_CONTRACTS, isActive: pathname => under(pathname, FINANCE_CONTRACTS) },
  { labelKey: 'nav.financeItems.debts', route: FINANCE_DEBTS, isActive: pathname => under(pathname, FINANCE_DEBTS) },
  { labelKey: 'nav.financeItems.settings', route: FINANCE_SETTINGS, isActive: pathname => under(pathname, FINANCE_SETTINGS) },
  {
    labelKey: 'nav.financeItems.platformItems',
    route: FINANCE_PLATFORM_ITEMS,
    isActive: pathname => under(pathname, FINANCE_PLATFORM_ITEMS),
    superAdminOnly: true
  }
];

/** Keyboard focus on the dark sidebar: the default ripple tint is invisible against it. */
export const SIDEBAR_FOCUS_SX = {
  '&.Mui-focusVisible': { outline: `2px solid ${palette.common.white}`, outlineOffset: '-2px' }
} as const;

const LIST_ID = 'sidebar-finance-items';

/** A square, icon-only item in the collapsed strip. */
export const COLLAPSED_ITEM_SX = {
  width: 48,
  height: 48,
  minWidth: 48,
  p: 0,
  mb: 0,
  borderRadius: '8px',
  justifyContent: 'center',
  flex: 'none',
  '&:hover': { backgroundColor: palette.sidebar.selected },
  ...SIDEBAR_FOCUS_SX
} as const;

/**
 * What both shapes of the Finance entry need: which items this user gets, which one is
 * current, and how to go to one carrying the kindergarten along. Shared so the wide
 * accordion and the icon-strip menu can never disagree about any of it.
 */
const useFinanceNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSuperAdmin = useUserStore(state => !!state.userInfo?.isSuperAdmin);

  const inFinance = under(location.pathname, FINANCE);
  const items = FINANCE_ITEMS.filter(item => !item.superAdminOnly || isSuperAdmin);
  const isActive = (item: FinanceNavItem) => item.isActive(location.pathname);
  const anyActive = items.some(isActive);
  const kindergartenId = inFinance ? new URLSearchParams(location.search).get('kindergartenId') : null;

  const go = (item: FinanceNavItem) => {
    // the platform catalogue belongs to no kindergarten, so it gets none
    const carry = kindergartenId && !item.superAdminOnly ? `?kindergartenId=${kindergartenId}` : '';
    navigate(`${item.route}${carry}`);
  };

  return { items, isActive, anyActive, inFinance, go };
};

/**
 * Finance as an accordion: the parent only opens and closes, and the page you are on is
 * the one item with a filled background - the parent turns bold instead, so there are
 * never two highlighted rows saying the same thing.
 *
 * It opens by itself whenever the route enters Finance, and otherwise stays the way the
 * admin left it - including across the sidebar collapsing and expanding again.
 *
 * Moving between Finance pages carries `?kindergartenId=` along: they are all about one
 * kindergarten, and dropping it on every click would make the admin pick it again.
 */
const SidebarFinanceGroup: React.FC = () => {
  const { t } = useTranslation();
  const { items, isActive, anyActive, inFinance, go } = useFinanceNav();
  const open = useSidebarStore(state => state.financeOpen);
  const setOpen = useSidebarStore(state => state.setFinanceOpen);

  useEffect(() => {
    if (inFinance) setOpen(true);
  }, [inFinance, setOpen]);

  return (
    <>
      <ListItemButton
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={LIST_ID}
        sx={{ height: 48, p: '0 12px 0 16px', ...SIDEBAR_FOCUS_SX }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          <ReceiptLongOutlinedIcon sx={{ width: 24, height: 24, color: 'white' }} />
          <ListItemText
            primary={t('nav.finance')}
            slotProps={{ primary: { sx: { color: 'white', fontSize: 16, fontWeight: anyActive ? 600 : 400 } } }}
          />
        </Box>
        <ExpandMoreIcon
          sx={{
            fontSize: 20,
            color: palette.sidebar.textMuted,
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(180deg)' : 'none'
          }}
        />
      </ListItemButton>

      {/* kept mounted while closed, so aria-controls always points at something */}
      <Collapse in={open} timeout="auto">
        <Box
          id={LIST_ID}
          role="group"
          aria-label={t('nav.finance')}
          sx={{ position: 'relative', pl: '40px', mt: '4px', mb: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}
        >
          {/* sits under the centre of the parent's icon, tying the items to it */}
          <Box
            aria-hidden
            sx={{ position: 'absolute', left: '27px', top: '4px', bottom: '4px', width: '1px', backgroundColor: 'rgba(255,255,255,0.2)' }}
          />
          {items.map(item => {
            const active = isActive(item);
            return (
              <ListItemButton
                key={item.route}
                selected={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => go(item)}
                sx={{
                  height: 40,
                  px: 2,
                  // the theme spaces every list button 8px apart; the gap above owns spacing here
                  mb: 0,
                  color: active ? palette.common.white : palette.sidebar.textMuted,
                  '&:hover': { color: palette.common.white },
                  ...SIDEBAR_FOCUS_SX
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: active ? 500 : 400, color: 'inherit' }}>{t(item.labelKey)}</Typography>
              </ListItemButton>
            );
          })}
        </Box>
      </Collapse>
    </>
  );
};

/**
 * Finance in the icon strip: one button, and the items in a menu beside it.
 *
 * The button is filled whenever any Finance page is current - with the items out of sight,
 * it is the only thing left that can say where you are.
 */
export const SidebarFinanceMenu: React.FC = () => {
  const { t } = useTranslation();
  const { items, isActive, anyActive, go } = useFinanceNav();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title={t('nav.finance')} placement="right">
        <ListItemButton
          ref={buttonRef}
          selected={anyActive}
          onClick={() => setOpen(true)}
          aria-label={t('nav.finance')}
          aria-haspopup="menu"
          aria-expanded={open}
          sx={COLLAPSED_ITEM_SX}
        >
          <ReceiptLongOutlinedIcon sx={{ width: 24, height: 24, color: 'white' }} />
        </ListItemButton>
      </Tooltip>
      <Menu
        anchorEl={buttonRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { elevation: 3, sx: { width: 232, borderRadius: '10px', ml: 1 } },
          list: { sx: { py: 1 }, 'aria-label': t('nav.finance') }
        }}
      >
        {/* a heading, not an option: not focusable and cannot be chosen */}
        <Box component="li" role="presentation" sx={{ p: '8px 16px 4px', fontSize: 12, color: palette.text.secondary, listStyle: 'none' }}>
          {t('nav.finance')}
        </Box>
        {items.map(item => {
          const active = isActive(item);
          return (
            <MenuItem
              key={item.route}
              aria-current={active ? 'page' : undefined}
              onClick={() => {
                setOpen(false);
                go(item);
              }}
              sx={{
                height: 44,
                px: 2,
                fontSize: 16,
                color: active ? palette.sidebar.main : palette.text.primary,
                fontWeight: active ? 500 : 400,
                backgroundColor: active ? palette.surface.tint : undefined,
                '&:hover': { backgroundColor: active ? palette.surface.tint : palette.surface.hover }
              }}
            >
              {t(item.labelKey)}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default SidebarFinanceGroup;
